/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import TourIcon from '@mui/icons-material/Tour'
import Collapse from '@mui/material/Collapse'
import { IconButton, Snackbar } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService'
import Alert from '@mui/material/Alert'
import { useDispatch, useSelector } from 'react-redux'
import ProjectInputs, { SubSectionTitle } from '../ProjectInputs/ProjectInputs'
import {
  collapseLegendAndBaseMap,
  setMapviewExtent,
  togglePopupTemplates,
  toggleErrPopupTemplates,
  togglePopup,
  zoomToSelectedFeature
} from '../../controller/esriMapController'
import { setDrawerOpen } from '../../slices/mainTool'
import CustomPopup from '../CustomPopup'
import {
  clearAllSelectedFeatures,
  setShowConfirmDeleteModal,
  setShowClearSegmentsStartManualModal,
  setShowErrRemovingFeatureFromProjectModal,
  setShowLanduseErrModal,
  setShowNoFeatureSelectedModal,
  setShowPopup,
  setShowResetTiersModal,
  setShowSegmentAddedToaster,
  setShowToolbox,
  setManualStreetMode,
  resetDefaultModalTiers
} from '../../slices/appSlice'
import { LightTooltip } from '../CriteriaSelection/ModalCriteria'
import { setStartAppTour } from '../../slices/tourSlice'
import SegmentSelectToolbox from '../SegmentSelectToolbox'
import LayerSelector, { FabWrapper, StyledFab } from './LayerSelector'
import MapViewContainer from './MapViewContainer'
import CustomModal from '../CustomModal'
import { ADD, REMOVE } from '../../constants/segmentSelectionToolbox'
import { themeConfig } from '../../config/theme.config'
import { segmentSelectionStartUp } from '../../slices/appSlice'
import {
  ADD_FEATURE_TO_PROJECT,
  ALREADY_PART_OF_PROJECT,
  DIFF_LANDUSE,
  DIFF_ST_NAME,
  DIFF_TIERS,
  NOT_PART_OF_PROJECT,
  REMOVE_FEATURE_FROM_PROJECT
} from '../../constants/popup'
import {
  resetModalTiers,
  setAllModalTiers
} from '../../slices/generalInputs/modalTiers'
import {
  resetRoadwayVariables,
  setRoadwayVariables
} from '../../slices/generalInputs/roadwayVariables'
import { NO } from '../../constants/roadwayVariables'
import { LANDUSE_CONTEXT, ST_NAME } from '../../constants/layerFieldNames'
import SubHeader from '../Headers/SubHeader'
import { resetModalPrioritization } from '../../slices/generalInputs/modalPrioritization'

export function arraysAreEqual(arr1, arr2) {
  // Check if the arrays have the same length
  if (arr1.length !== arr2.length) {
    return false
  }

  // Check each element one by one
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false
    }
  }

  // If all elements are equal, return true
  return true
}

function MapView() {
  const [openAlert, setOpenAlert] = useState(true)
  const [segmentAlertText, setSegmentAlertText] = useState('')
  const { drawerOpen } = useSelector((state) => state.mainTool)
  const { roadway_name } = useSelector((state) => state.roadwayVariables)
  const {
    mapLoaded,
    manualStreetMode,
    showPopup,
    selectedFeature,
    showToolbox,
    selectedFeaturesArr,
    showNoFeatureSelectedModal,
    showSegmentAddedToaster,
    showConfirmDeleteModal,
    showClearSegmentsStartManualModal,
    notMatchingVariables,
    shareableURL,
    defaultModalTiers,
    showResetTiersModal,
    popupSelectedFeature
  } = useSelector((state) => state.appSlice)
  const { activeTool } = useSelector((state) => state.segmentSelectionToolbox)
  const dispatch = useDispatch()
  const [popupAttr, setPopupAttr] = useState(null)
  const [notMatchingVariablesFeature, setNotMatchingVariablesFeature] =
    useState([])

  useEffect(() => {
    if (notMatchingVariables.length) {
      setNotMatchingVariablesFeature(
        notMatchingVariables.map((variable) =>
          variable.slice(
            0,
            variable.indexOf('(') === -1
              ? variable.length
              : variable.indexOf('(')
          )
        )
      )
    }
  }, [notMatchingVariables])

  useEffect(() => {
    if (mapLoaded) {
      if (drawerOpen) {
        setMapviewExtent()
      }

      // Collapse popup when drawer open
      drawerOpen ? togglePopup(true) : togglePopup(false)
    }
  }, [drawerOpen, mapLoaded])

  useEffect(() => {
    // Update alert text whenever activet ool changed
    setSegmentAlertText(
      activeTool === ADD && selectedFeaturesArr.length
        ? `New Segment added to ${roadway_name} project.`
        : `Segment removed from ${roadway_name} project.`
    )
  }, [activeTool, roadway_name, selectedFeaturesArr])
  useEffect(() => {
    // Update popup button text whenever active tool changed, and selcted popup feature changes

    const esriPopupBtns = document.querySelectorAll('.setup-project-btn')

    if (esriPopupBtns.length) {
      esriPopupBtns.forEach((btn) => {
        btn.innerText =
          activeTool === ADD
            ? ADD_FEATURE_TO_PROJECT
            : REMOVE_FEATURE_FROM_PROJECT
      })
    }
  }, [activeTool])
  useEffect(() => {
    if (mapLoaded && popupSelectedFeature) {
      if (!selectedFeaturesArr.length) {
        // Update popup button text whenever active tool changed, and selcted popup feature changes
        togglePopupTemplates(activeTool)
      } else {
        // Check if the selected Feature is already part of your project
        const info = segmentSelectionStartUp(
          { selectedFeaturesArr },
          { payload: popupSelectedFeature }
        )

        const segmentsToAdd = !info.currentSegmentIds.has(
          info.incomingSegmentId
        )
          ? [info.incomingSegment]
          : []
        if (activeTool === ADD) {
          const firstSegmentStreetName =
            selectedFeaturesArr[0].attributes?.[ST_NAME]

          const selectedFeatureStreetName =
            popupSelectedFeature?.attributes?.[ST_NAME]

          const selectedFeatureLanduseContext =
            popupSelectedFeature?.attributes[LANDUSE_CONTEXT]

          const firstSegmentLanduseContext =
            selectedFeaturesArr[0].attributes[LANDUSE_CONTEXT]

          const streetTierArr = [
            'Ped_Tier',
            'Bike_Tier',
            'Transit_Tier',
            'Auto_Tier',
            'Freight_Tier'
          ]

          const selectedFeatureTiers = streetTierArr.map(
            (tier) => popupSelectedFeature?.attributes[tier]
          )

          const firstSegmentTiers = streetTierArr.map(
            (tier) => selectedFeaturesArr[0].attributes[tier]
          )

          // If the incoming feature is already part of the redux state, then return.
          if (!segmentsToAdd.length) {
            toggleErrPopupTemplates(ALREADY_PART_OF_PROJECT)
          } else if (selectedFeatureStreetName !== firstSegmentStreetName) {
            // if its not already part of your project, check if it has same street name or not
            toggleErrPopupTemplates(DIFF_ST_NAME)
          } else if (
            selectedFeatureLanduseContext !== firstSegmentLanduseContext
          ) {
            // if its not already part of your project, check if it has different landuse or not
            toggleErrPopupTemplates(DIFF_LANDUSE)
          } else if (
            arraysAreEqual(selectedFeatureTiers, firstSegmentTiers) === false
          ) {
            // if any of the tiers are different then show err modal
            toggleErrPopupTemplates(DIFF_TIERS)
          } else {
            // If its not already part of your project, and has same street name same landuse, then add it to your project
            togglePopupTemplates(ADD)
          }
        } else if (activeTool === REMOVE) {
          // Show err not if selected feature trying to be removed, is not already part of your project
          if (segmentsToAdd.length) {
            toggleErrPopupTemplates(NOT_PART_OF_PROJECT)
          } else {
            // If its already part of your project, then remove it from your project
            togglePopupTemplates(REMOVE)
          }
        }
      }
    }
  }, [popupSelectedFeature, mapLoaded, selectedFeaturesArr, activeTool])

  useEffect(() => {
    if (selectedFeature) {
      dispatch(setShowPopup(true))
      setPopupAttr(selectedFeature.attributes)
      // If a feature is selected, hide alert
      setOpenAlert(false)
    } else {
      // If no selectedFeature, hide popup
      dispatch(setShowPopup(false))
      // If no selectedFeature, hide drawer
      !manualStreetMode && dispatch(setDrawerOpen(false))
    }
  }, [selectedFeature, manualStreetMode])

  useEffect(() => {
    // in case of manual street mode, hide this alert
    if (manualStreetMode) {
      setOpenAlert(false)
    } else {
      !selectedFeature && setOpenAlert(true)
    }
  }, [manualStreetMode, selectedFeature])

  useEffect(() => {
    // When popup open add disabled class to esri base map gallery widget
    if (mapLoaded) {
      // const esriExpandElement = document.querySelectorAll(
      //   '.esri-expand__container'
      // )
      // showPopup
      //   ? esriExpandElement?.forEach((elm) => elm.classList?.add('disabled'))
      //   : esriExpandElement?.forEach((elm) => elm.classList?.remove('disabled'))
      // Once map is loaded add class names to basemap gallery widget and legend widget so that you can use in tour

      const esriExpandElements = document.querySelectorAll(
        '.esri-expand__container'
      )
      const classNamesArr = ['basemapWidget', 'legendWidget']
      esriExpandElements[0]?.classList?.add(classNamesArr[0])
      esriExpandElements[1]?.classList?.add(classNamesArr[1])
    }
  }, [mapLoaded])

  useEffect(() => {
    // If toolbox is open close legend
    if (showToolbox && mapLoaded) {
      collapseLegendAndBaseMap()
    }
  }, [showToolbox, mapLoaded])

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }

    dispatch(setShowSegmentAddedToaster(false))
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      {/* {showLanduseErrModal && (
        <CustomModal
          title="An Error has occured !"
          btnText="Zoom To Selected Features"
          handleBtnClick={() => {
            zoomToSelectedFeature()
          }}
          modifiedModalContent={
            <SubSectionTitle fontWeight={400} fontSize={14}>
              You have selected road segments with different{' '}
              <strong>{notMatchingVariables.join(', ')} </strong>. All segments
              part of the project should have the same{' '}
              <strong>{notMatchingVariablesFeature.join(', ')} </strong>.
            </SubSectionTitle>
          }
        />
      )} */}
      {/* {showErrAddingFeatureToProjectModal && (
        <CustomModal
          title="Segment is already part of your project !"
          btnText="OK"
          handleBtnClick={() =>
            dispatch(setShowErrAddingFeatureToProjectModal(false))
          }
          modalContent="The selected segment is already part of your project. Please select another segment."
        />
      )} */}
      {/* {showErrRemovingFeatureFromProjectModal && (
        <CustomModal
          title="Error, no segments to remove!"
          btnText="OK"
          handleBtnClick={() =>
            dispatch(setShowErrRemovingFeatureFromProjectModal(false))
          }
          modalContent="
          You haven't included any segments in your project that can be removed. Please choose and add segments to your project."
        />
      )} */}
      {showResetTiersModal && (
        <CustomModal
          title="Are you sure you want to reset?"
          btnText="CONFIRM"
          justifyContent="flex-end"
          handleBtnClick={() => {
            dispatch(setAllModalTiers(defaultModalTiers))
            dispatch(
              setRoadwayVariables({
                field: 'high_capacity_transit',
                value: NO
              })
            )
            dispatch(setShowResetTiersModal(false))
          }}
          showCancelBtn={true}
          modalContent="Pressing the 'Confirm' will revert all changes made to your modal tiers and priorities to their deafult values."
        />
      )}
      {showNoFeatureSelectedModal && (
        <CustomModal
          title="No Features were selected !"
          btnText="OK"
          handleBtnClick={() => {
            dispatch(setShowNoFeatureSelectedModal(false))
          }}
          modalContent="No feature has been selected, consider zooming in closer to the desired feature and then selecting it once more."
        />
      )}
      {showConfirmDeleteModal && (
        <CustomModal
          title="Starting Over?"
          btnText="CONFIRM"
          justifyContent="flex-end"
          handleBtnClick={() => {
            // When all segments are cleared close popup and close drawer
            dispatch(setShowPopup(false))
            dispatch(clearAllSelectedFeatures())

            dispatch(setDrawerOpen(false))
            dispatch(setShowConfirmDeleteModal(false))
          }}
          showCancelBtn={true}
          modalContent="Proceeding with this will clear all segments from your current project. Please click “Confirm” to continue."
        />
      )}
      {showClearSegmentsStartManualModal && (
        <CustomModal
          title="Shift to Manual Project Setup?"
          btnText="CONFIRM"
          justifyContent="flex-end"
          handleBtnClick={() => {
            // When all segments are cleared close popup and close drawer
            dispatch(setShowPopup(false))
            dispatch(clearAllSelectedFeatures())

            dispatch(setShowClearSegmentsStartManualModal(false))
            dispatch(setManualStreetMode(true))
          }}
          showCancelBtn={true}
          modalContent="Proceeding with this will clear all segments from your current project and start a Manual project for you. Please click “Confirm” to continue."
        />
      )}
      <Collapse in={openAlert}>
        <Alert
          severity="info"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setOpenAlert(false)
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            mb: 2,
            width: '30vw',
            position: 'absolute',
            top: 70,
            left: '35%',
            backgroundColor: themeConfig.semcogBlue1,
            color: 'white',
            zIndex: 1050,
            '& .MuiAlert-message': {
              alignSelf: 'center'
            },
            '& .MuiAlert-icon': {
              color: 'white !important'
            }
          }}
        >
          Zoom into your project location on the map, or use the search bar to
          find an address. Click roadway segments to select them and get
          started! Or you can choose to setup your project manually.
        </Alert>
      </Collapse>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showSegmentAddedToaster}
        autoHideDuration={2000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          sx={{
            mb: 2,
            width: '30vw',
            position: 'absolute',
            color: 'white',
            top: 70,
            backgroundColor: themeConfig.semcogBlue1,
            zIndex: 1050,
            '& .MuiAlert-message': {
              alignSelf: 'center'
            },
            '& .MuiAlert-icon': {
              color: 'white !important'
            }
          }}
        >
          {segmentAlertText}
        </Alert>
      </Snackbar>
      {mapLoaded && <LayerSelector />}
      <SubHeader top={63} left={15} className="manualProjectSetup" />
      {showPopup && <CustomPopup attributes={popupAttr} />}
      {showToolbox && <SegmentSelectToolbox />}
      <MapViewContainer />

      <SwipeableDrawer
        anchor="bottom"
        hideBackdrop={true}
        variant="persistent"
        open={drawerOpen}
        onClose={() => {
          dispatch(setDrawerOpen(false))
        }}
        onOpen={() => {
          dispatch(setDrawerOpen(true))
        }}
        transitionDuration={{ enter: 700, exit: 500 }}
        sx={{
          '& .MuiPaper-root': {
            borderTopLeftRadius: '20px !important',
            borderTopRightRadius: '20px !important',
            zIndex: manualStreetMode && 100001
          }
        }}
      >
        <Box
          sx={{
            height: '550px'
          }}
        >
          <ProjectInputs />
        </Box>
      </SwipeableDrawer>
      {/** ToolBox Icon */}
      <FabWrapper top="260px" right="15px" width={40}>
        <LightTooltip title="Segment Selection Toolbox" placement="bottom">
          <Box className="segmentSelectToolbox">
            <StyledFab
              color="primary"
              aria-label="add"
              onClick={(e) => {
                dispatch(setShowToolbox(!showToolbox))
              }}
              disableRipple={true}
              borderradius="50%"
              width={40}
              height={40}
            >
              <HomeRepairServiceIcon sx={{ width: 20, height: 20 }} />
            </StyledFab>
          </Box>
        </LightTooltip>
      </FabWrapper>
      {/** Take Tour Icon */}
      <FabWrapper right="15px">
        <Box className="tourApp">
          <LightTooltip title="Click here to take the tour" placement="right">
            <StyledFab
              color="primary"
              aria-label="add"
              onClick={() => {
                dispatch(setStartAppTour(true))
              }}
              disableRipple={true}
              borderradius="50%"
            >
              <TourIcon sx={{ width: 30, height: 30 }} />
            </StyledFab>
          </LightTooltip>
        </Box>
      </FabWrapper>
    </Box>
  )
}

export default MapView
